(function() {
  'use strict';

  /* eslint-env browser, es2020 */

  // Tracks whether a fatal error panel is being shown so the readiness
  // observer never hides the overlay over the top of an error message.
  var errorShown = false;
  var loaderHidden = false;
  // Once the app shell has rendered, a stray error or unhandled rejection is no
  // longer fatal - blanking the whole UI at that point loses the user's work
  // and, in e2e runs, hides the app behind an overlay for the rest of the test.
  var appBooted = false;

  function updateResetAppMsgVisibility(visibility) {
    var elem = document.getElementById('msg-for-impatient');
    if (elem !== null) { elem.style.visibility = visibility; }
  }

  function updateResetAppMsgContent(content) {
    var elem = document.getElementById('msg-for-impatient');
    if (elem !== null) { elem.textContent = content; }
  }

  // ---------------------------------------------------------------------------
  // Loader lifecycle
  // ---------------------------------------------------------------------------

  function hideLoader() {
    if (loaderHidden || errorShown) { return; }
    loaderHidden = true;
    appBooted = true;
    var loader = document.getElementById('app-loader');
    if (!loader) { return; }
    loader.classList.add('app-loader--hide');
    setTimeout(function() {
      if (loader && loader.parentNode) { loader.parentNode.removeChild(loader); }
    }, 400);
  }

  // The overlay should stay until the real application shell or routed content
  // has rendered, bridging the gap between Angular bootstrap (which empties
  // <app-root>) and the menu/first view actually painting.
  function appHasRenderedContent() {
    // Preferred signal: the application shell menu has rendered children.
    var menu = document.getElementById('header-menu');
    if (menu && menu.children.length > 0) { return true; }

    // Fallback (covers routes without the menu, e.g. login): any routed
    // component element rendered inside <app-root> with a visible box.
    var root = document.querySelector('app-root');
    if (root) {
      var kids = root.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        var isOutlet = el.tagName && el.tagName.toLowerCase() === 'router-outlet';
        if (!isOutlet && el.getClientRects && el.getClientRects().length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  function startReadinessObserver() {
    if (typeof MutationObserver === 'undefined') {
      // Old browser: fall back to the safety timeout above.
      return;
    }
    var observer = new MutationObserver(function() {
      if (errorShown) { observer.disconnect(); return; }
      if (appHasRenderedContent()) {
        observer.disconnect();
        hideLoader();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // Check immediately in case content already rendered.
    if (appHasRenderedContent()) {
      observer.disconnect();
      hideLoader();
    }
  }

  // ---------------------------------------------------------------------------
  // Error panel
  // ---------------------------------------------------------------------------

  function setText(id, text) {
    var el = document.getElementById(id);
    if (el) { el.textContent = text; }
  }

  // Central error renderer. Called by window.onerror, the unhandledrejection
  // handler, and the Angular bootstrap .catch() in main.ts (via window.kzShowError).
  function showError(message, source, lineno, colno, error) {
    if (errorShown) { return; }
    errorShown = true;

    var timestamp = new Date().toISOString();
    var errorMessage = message || 'Unknown error';

    var fullDetails = '=== risr/advance Error Report ===\n\n';
    fullDetails += 'Time: ' + timestamp + '\n';
    fullDetails += 'Error: ' + errorMessage + '\n';
    if (source) { fullDetails += 'Source: ' + source + '\n'; }
    if (lineno) { fullDetails += 'Line: ' + lineno + '\n'; }
    if (colno) { fullDetails += 'Column: ' + colno + '\n'; }
    fullDetails += 'URL: ' + window.location.href + '\n';
    fullDetails += 'User Agent: ' + navigator.userAgent + '\n';
    if (error && error.stack) { fullDetails += '\nStack Trace:\n' + error.stack; }

    setText('error-message', errorMessage);
    setText('error-timestamp', timestamp);
    setText('error-useragent', navigator.userAgent);
    setText('error-url', window.location.href);

    if (source || lineno || colno) {
      var errorLocationDiv = document.getElementById('error-location');
      if (errorLocationDiv) { errorLocationDiv.style.display = 'block'; }
      if (source) { setText('error-source', 'File: ' + source); }
      if (lineno || colno) {
        var locationText = 'Line: ' + (lineno || '?');
        if (colno) { locationText += ', Column: ' + colno; }
        setText('error-linecol', locationText);
      }
    }

    if (error && error.stack) {
      var errorStackContainer = document.getElementById('error-stack-container');
      if (errorStackContainer) { errorStackContainer.style.display = 'block'; }
      setText('error-stack', error.stack);
    }

    setText('error-full-details', fullDetails);

    // Make sure the overlay is visible (cancel any in-flight fade) and swap
    // the loading content for the error panel.
    var loader = document.getElementById('app-loader');
    if (loader) { loader.classList.remove('app-loader--hide'); }

    var loadingContent = document.getElementById('inner-content');
    if (loadingContent) { loadingContent.style.display = 'none'; }
    updateResetAppMsgVisibility('hidden');

    var errorContainer = document.getElementById('error-container');
    if (errorContainer) { errorContainer.style.display = 'block'; }
  }

  // Public hook used by main.ts when Angular bootstrap rejects. Always fatal.
  window.kzShowError = function(err) {
    var message = (err && err.message) ? err.message : String(err);
    showError(message, undefined, undefined, undefined, err);
  };

  window.onerror = function(message, source, lineno, colno, error) {
    if (appBooted) {
      console.error('Uncaught error after boot', message, source, lineno, colno, error);
      return false;
    }
    showError(message, source, lineno, colno, error);
    return true;
  };

  window.addEventListener('unhandledrejection', function(event) {
    var reason = event ? event.reason : undefined;
    var message = (reason && reason.message)
      ? reason.message
      : ('Unhandled promise rejection: ' + String(reason));
    if (appBooted) {
      console.error('Unhandled rejection after boot', reason);
      return;
    }
    showError(message, undefined, undefined, undefined, reason);
  });

  // ---------------------------------------------------------------------------
  // Service worker reset (the "Reload the application" action)
  // ---------------------------------------------------------------------------

  function unregisterServiceWorker() {
    updateResetAppMsgContent('Resetting App...');
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then(function(registrations) {
          var i,
              proms = [];
          for (i = 0; i < registrations.length; i++) {
            proms.push(registrations[i].unregister());
          }
          return Promise.all(proms);
        })
        .then(function() { window.location.reload(); })
        .catch(function(error) {
          updateResetAppMsgContent('Error resetting App: ' + error);
        });
    } else {
      window.location.reload();
    }
  }

  // ---------------------------------------------------------------------------
  // Clipboard helper for the error panel
  // ---------------------------------------------------------------------------

  function flashCopied() {
    var btn = document.getElementById('copy-btn');
    if (!btn) { return; }
    var originalText = btn.innerHTML;
    btn.innerHTML = '✓ Copied!';
    btn.style.backgroundColor = '#10b981';
    setTimeout(function() {
      btn.innerHTML = originalText;
      btn.style.backgroundColor = '#3b82f6';
    }, 2000);
  }

  function copyErrorDetails() {
    var copyFailMsg = 'Could not copy. Please select and copy the error details manually.';
    var errorElement = document.getElementById('error-full-details');
    if (!errorElement) {
      alert('Error details element not found. Please try again.');
      return;
    }
    var errorText = errorElement.textContent || errorElement.innerText;
    if (!errorText) {
      alert('No error text to copy. Please try again.');
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(errorText).then(flashCopied).catch(function() {
        alert(copyFailMsg);
      });
    } else {
      try {
        var textArea = document.createElement('textarea');
        textArea.value = errorText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        var successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          flashCopied();
        } else {
          alert(copyFailMsg);
        }
      } catch (err) {
        alert(copyFailMsg);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Wire-up
  // ---------------------------------------------------------------------------

  function wireButtons() {
    var unregisterBtn = document.getElementById('unregister-btn');
    if (unregisterBtn) {
      unregisterBtn.addEventListener('click', function() { unregisterServiceWorker(); });
    }

    var reloadBtn = document.getElementById('reload-btn');
    if (reloadBtn) {
      reloadBtn.addEventListener('click', function() { window.location.reload(); });
      reloadBtn.addEventListener('mouseover', function() {
        reloadBtn.style.backgroundColor = '#2563eb';
      });
      reloadBtn.addEventListener('mouseout', function() {
        reloadBtn.style.backgroundColor = '#3b82f6';
      });
    }

    var copyBtn = document.getElementById('copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', function() { copyErrorDetails(); });
      copyBtn.addEventListener('mouseover', function() {
        copyBtn.style.backgroundColor = '#2563eb';
      });
      copyBtn.addEventListener('mouseout', function() {
        copyBtn.style.backgroundColor = '#3b82f6';
      });
    }
  }

  function init() {
    wireButtons();
    startReadinessObserver();
  }

  // Show the "this is taking a while" hint after 20s (was 60s).
  setTimeout(function() { updateResetAppMsgVisibility('visible'); }, 20000);

  // Safety net: never trap a working app behind the overlay. If readiness was
  // not detected for any reason, reveal whatever rendered after 30s.
  setTimeout(function() { if (!errorShown) { hideLoader(); } }, 30000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
